import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { updateConfig } from "../../../../store/slices/Delivery/invoicesSlice";
import {
  useGetAvailableInvoicesListDataQuery,
  useGetSupplierPaymentListDataQuery,
} from "../../../../store/queries/Delivery";
import useReceiptPrint from "../useReceiptPrint";
import useTabFilters from "../useTabFilters";

const usePending = () => {
  const paginationOptions = [
    { label: "10 Per page", value: 10 },
    { label: "20 Per page", value: 20 },
    { label: "30 Per page", value: 30 },
  ];

  const dispatch = useDispatch();
  const location = useLocation();
  const { receiptRef, triggerReceiptPrint } = useReceiptPrint();
  const reduxState = useSelector((state) => state.deliveryInvoices);
  const queryState = reduxState?.pendingTab;

  const searchParams = new URLSearchParams(location.search);
  const subTab = searchParams.get("subTab") || "invoices";
  const isInvoices = subTab === "invoices";

  const {
    date,
    formattedDate,
    handlePageSize,
    handlePagination,
    handleSort,
    handleSearch,
    handleDateChange,
  } = useTabFilters("pendingTab");

  // Invoices sub-tab — pending invoices available for a cash receipt
  const {
    data: invoiceList = {},
    isLoading: isLoadingInvoice,
    isFetching: isFetchingInvoice,
  } = useGetAvailableInvoicesListDataQuery(
    {
      page_size: queryState?.currentPageSize,
      page: queryState?.currentPage,
      sort_by: queryState?.sortBy,
      sort_order: queryState?.sortOrder,
      search: queryState?.search,
      from_date: formattedDate[0],
      to_date: formattedDate[1],
    },
    { skip: !isInvoices },
  );

  // Supplier sub-tab — active suppliers for a supplier payment
  const {
    data: supplierList = {},
    isLoading: isLoadingSupplier,
    isFetching: isFetchingSupplier,
  } = useGetSupplierPaymentListDataQuery(
    {
      per_page: queryState?.currentPageSize,
      page: queryState?.currentPage,
      sort_by: queryState?.sortBy,
      sort_order: queryState?.sortOrder,
      search: queryState?.search,
    },
    { skip: isInvoices },
  );

  const currentData = isInvoices ? invoiceList : supplierList;
  const isFetching = isInvoices ? isFetchingInvoice : isFetchingSupplier;
  const isLoading = isInvoices ? isLoadingInvoice : isLoadingSupplier;
  const tableFields = currentData?.data?.fields;

  // Reset selection / pagination whenever the sub-tab changes
  useEffect(() => {
    dispatch(
      updateConfig((state) => {
        state.pendingSubTab = subTab;
        state.selectedInvoiceIds = [];
        state.selectedSupplier = null;
        state.pendingTab.clear = true;
        state.pendingTab.currentPage = 1;
      }),
    );
    Promise.resolve().then(() => {
      dispatch(
        updateConfig((state) => {
          state.pendingTab.clear = false;
        }),
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subTab]);

  const subTabNavs = [
    {
      label: "Invoices",
      title: "Invoices",
      link: `/orders/delivery/invoices?activeTab=pending&subTab=invoices`,
      active: subTab === "invoices",
    },
    {
      label: "Supplier/Shop",
      title: "Supplier/Shop",
      link: `/orders/delivery/invoices?activeTab=pending&subTab=supplier`,
      active: subTab === "supplier",
    },
  ];

  const getRow = (field, data) => {
    const renderers = {
      common: (f, d) => <p className="pro-mb-0">{d[f]}</p>,
    };
    return (renderers[field] ?? renderers.common)(field, data);
  };

  const clearPendingSelection = () => {
    dispatch(
      updateConfig((state) => {
        state.pendingTab.clear = true;
      }),
    );
    Promise.resolve().then(() => {
      dispatch(
        updateConfig((state) => {
          state.pendingTab.clear = false;
        }),
      );
    });
  };

  // Invoices slip modal (multi select)
  const handleOpenReceiptModal = (selectedIds) => {
    if (!selectedIds?.length) {
      toast.warn("Select at least one invoice");
      return;
    }
    dispatch(
      updateConfig((state) => {
        state.selectedInvoiceIds = selectedIds;
        state.summaryData = null;
        state.showReceiptModal = true;
      }),
    );
  };

  const handleCloseReceiptModal = () => {
    dispatch(
      updateConfig((state) => {
        state.showReceiptModal = false;
        state.selectedInvoiceIds = [];
        state.summaryData = null;
      }),
    );
    clearPendingSelection();
  };

  // Supplier payment modal (single select)
  const handleOpenSupplierModal = (selectedIds) => {
    const id = selectedIds?.[0];
    if (!id) {
      toast.warn("Select a supplier");
      return;
    }
    const supplier = (currentData?.data?.data || []).find(
      (row) => row?._id === id,
    );
    dispatch(
      updateConfig((state) => {
        state.selectedSupplier = supplier
          ? { _id: supplier._id, name: supplier.name }
          : { _id: id, name: "" };
        state.showSupplierModal = true;
      }),
    );
  };

  const handleCloseSupplierModal = () => {
    dispatch(
      updateConfig((state) => {
        state.showSupplierModal = false;
        state.selectedSupplier = null;
      }),
    );
    clearPendingSelection();
  };

  return {
    paginationOptions,
    subTab,
    isInvoices,
    subTabNavs,
    reduxState,
    queryState,
    currentData,
    isFetching,
    isLoading,
    tableFields,
    receiptRef,
    date,
    getRow,
    handleDateChange,
    handleSearch,
    handleSort,
    handlePageSize,
    handlePagination,
    handleOpenReceiptModal,
    handleCloseReceiptModal,
    handleOpenSupplierModal,
    handleCloseSupplierModal,
    triggerReceiptPrint,
  };
};

export default usePending;
